import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { TaskFilters, TaskFiltersInline } from '@/components/tasks/TaskFilters';
import { TasksTable } from '@/components/tasks/TasksTable';
import { TasksCardGrid } from '@/components/tasks/TaskCard';
import { ViewToggle, useViewPreference } from '@/components/ui/view-toggle';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import { tasksService } from '@/services/tasks.service';
import { clientsService } from '@/services/clients.service';
import { projectsService } from '@/services/projects.service';
import { useToast } from '@/components/ui/Toast';
import type { Priority, TaskStatus, Task, Client, Project, TaskOrigin } from '@/types';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, ClipboardList, Search } from 'lucide-react';

function TasksContent() {
  const { toast } = useToast();
  const [view, setView] = useViewPreference('tasks-view', 'table');
  const [searchQuery, setSearchQuery] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<Priority | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [originFilter, setOriginFilter] = useState<TaskOrigin | 'all'>('all');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteTask, setDeleteTask] = useState<Task | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [tasksData, clientsData, projectsData] = await Promise.all([
        tasksService.getTasks(),
        clientsService.getClients(),
        projectsService.getProjects(),
      ]);
      setTasks(tasksData);
      setClients(clientsData);
      setProjects(projectsData);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTask) return;
    try {
      setIsDeleting(true);
      await tasksService.deleteTask(deleteTask.id);
      setTasks((prev) => prev.filter((t) => t.id !== deleteTask.id));
      toast.success('Task deleted successfully');
    } catch (err: any) {
      console.error('Error deleting task:', err);
      toast.error(err.message || 'Failed to delete task');
    } finally {
      setIsDeleting(false);
      setDeleteTask(null);
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter((task) => {
      const matchesSearch =
        searchQuery === '' ||
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesClient = clientFilter === 'all' || task.clientId === clientFilter;
      const matchesProject = projectFilter === 'all' || task.projectId === projectFilter;
      const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
      const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
      const matchesOrigin = originFilter === 'all' || (task.origin || 'flowky') === originFilter;

      return matchesSearch && matchesClient && matchesProject && matchesPriority && matchesStatus && matchesOrigin;
    });
  }, [tasks, searchQuery, clientFilter, projectFilter, priorityFilter, statusFilter, originFilter]);

  const emptyState = (
    <div className="text-center py-12 border border-border rounded-lg">
      <ClipboardList className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold text-foreground mb-2">No tasks found</h3>
      <p className="text-muted-foreground mb-4">
        {searchQuery || clientFilter !== 'all' || projectFilter !== 'all' || priorityFilter !== 'all' || statusFilter !== 'all'
          ? 'Try adjusting your filters'
          : 'Get started by adding your first task'}
      </p>
      {!searchQuery && clientFilter === 'all' && projectFilter === 'all' && priorityFilter === 'all' && statusFilter === 'all' && (
        <Button asChild>
          <Link to="/tasks/new">
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Task
          </Link>
        </Button>
      )}
    </div>
  );

  return (
    <AppLayout>
      <Breadcrumb items={[{ label: 'Tasks' }]} />

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive">
          {error}
        </div>
      )}

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-96" />
        </div>
      ) : (
        <>
          {/* Toolbar - Inline search and filters */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Inline Filters */}
            <TaskFiltersInline
              clients={clients}
              projects={projects}
              clientFilter={clientFilter}
              projectFilter={projectFilter}
              priorityFilter={priorityFilter}
              statusFilter={statusFilter}
              onClientChange={setClientFilter}
              onProjectChange={setProjectFilter}
              onPriorityChange={setPriorityFilter}
              onStatusChange={setStatusFilter}
            />

            {/* Spacer */}
            <div className="flex-1" />

            {/* Add Button */}
            <Button asChild>
              <Link to="/tasks/new">
                <Plus className="w-4 h-4" />
              </Link>
            </Button>

            {/* View Toggle */}
            <ViewToggle view={view} onViewChange={setView} onRefresh={loadData} />
          </div>

          {/* Active Filters Badges */}
          <TaskFilters
            clients={clients}
            projects={projects}
            clientFilter={clientFilter}
            projectFilter={projectFilter}
            priorityFilter={priorityFilter}
            statusFilter={statusFilter}
            originFilter={originFilter}
            onClientChange={setClientFilter}
            onProjectChange={setProjectFilter}
            onPriorityChange={setPriorityFilter}
            onStatusChange={setStatusFilter}
            onOriginChange={setOriginFilter}
          />

          {/* Content */}
          {view === 'table' ? (
            <TasksTable
              tasks={filteredTasks}
              clients={clients}
              projects={projects}
              isLoading={loading}
              onDelete={setDeleteTask}
              emptyState={emptyState}
            />
          ) : (
            <TasksCardGrid
              tasks={filteredTasks}
              clients={clients}
              projects={projects}
              emptyState={emptyState}
            />
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTask}
        onClose={() => setDeleteTask(null)}
        onConfirm={handleDelete}
        title="Delete Task"
        description={`Are you sure you want to delete "${deleteTask?.title}"? This action cannot be undone.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        type="danger"
      />
    </AppLayout>
  );
}

export default function TasksPage() {
  return (
    <ProtectedRoute>
      <TasksContent />
    </ProtectedRoute>
  );
}
